<script setup lang="ts">
import type { Component } from 'vue'
import { DEFAULT_TEXT_SETTINGS, DEFAULT_BACKGROUND_SETTINGS, DEFAULT_AUDIO_SETTINGS, DEFAULT_THEME_SETTINGS, DEFAULT_SECTION_SETTINGS, DEFAULT_CURSOR_SETTINGS, DEFAULT_WIDGET_SETTINGS, DEFAULT_COMMENT_SETTINGS, DEFAULT_YOUTUBE_EMBED_SETTINGS, DEFAULT_TWITCH_EMBED_SETTINGS, DEFAULT_SPOTIFY_EMBED_SETTINGS } from '~/stores/auth'
import type { CharacterPageData, CharacterPageSettings, AudioPlaylistItem, ClipDisplayItem, FriendDisplayItem } from '~/composables/useCharacterPage'
import type { LinkItem } from '~/stores/links'
import type { CharacterAchievementItem } from '~/stores/achievements'
import type { AchievementDefinition } from '~/stores/achievementDefinitions'
import type { GameItem } from '~/stores/games'
import type { GameDefinition } from '~/stores/gameDefinitions'

import type { SetupItem } from '~/stores/setups'
import DefaultLayout from '~/components/character/layout/DefaultLayout.vue'
import CompactLayout from '~/components/character/layout/CompactLayout.vue'
import SplitLayout from '~/components/character/layout/SplitLayout.vue'
import GridLayout from '~/components/character/layout/GridLayout.vue'

const LAYOUT_MAP: Record<string, Component> = {
  default: DefaultLayout,
  compact: CompactLayout,
  split: SplitLayout,
  grid: GridLayout
}

definePageMeta({
  layout: 'character'
})

// Signal to embed components that this is a skin preview (show placeholders, don't fetch)
provide('skinPreview', true)

const route = useRoute()
const router = useRouter()
const skinId = computed(() => route.params.id as string)

// The skin page is public and reachable from the homepage, the /skins gallery,
// search, or the dashboard. "Back" should return to wherever the visitor came
// from, and fall back to the public gallery when there's no in-app history
// (direct link, new tab) so it never dead-ends on a dashboard/login redirect.
function goBack() {
  const back = router.options.history.state.back
  if (typeof back === 'string' && back.startsWith('/')) {
    router.back()
  } else {
    navigateTo('/skins')
  }
}

// Fetch skin detail + real achievement/game definitions in parallel
// useFetch for skin endpoint ensures auth cookies are forwarded during SSR (needed for private skin access)
const [{ data: skin, error }, { data: achievementData }, { data: gameData }] = await Promise.all([
  useFetch<{
    id: string
    slug: string
    name: string
    creator_id: string
    creator_username: string | null
    required_plan: 'free' | 'pro' | 'elite'
    tags: string[]
    settings: {
      layout?: string
      text?: Record<string, unknown>
      background?: Record<string, unknown>
      audio?: Record<string, unknown>
      theme?: Record<string, unknown>
      cursor?: Record<string, unknown>
      sections?: Record<string, unknown>
      widgets?: Record<string, any>
    }
    avatar_url: string | null
    background_url: string | null
    cursor_url: string | null
    custom_font_url: string | null
    font_original_name: string | null
    audio_tracks: { id: string, original_name: string, position: number, url: string | null }[]
    use_count: number
    created_at: string
  }>(`/api/skins/${skinId.value}`, { key: `skin-preview-${skinId.value}` }),
  useAsyncData('skin-preview-achievements', () => $fetch<{ achievements: AchievementDefinition[] }>('/api/achievements')),
  useAsyncData('skin-preview-games', () => $fetch<{ games: GameDefinition[] }>('/api/games'))
])

if (error.value || !skin.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Skin not found'
  })
}

// Redirect a bare-UUID or stale-name URL to the canonical keyword slug (301).
if (route.params.id !== skin.value.slug) {
  await navigateTo(`/skins/${skin.value.slug}`, { redirectCode: 301, replace: true })
}

// SEO. "free" only when the skin is actually free-plan; skins without a preview
// background are noindex,follow (thin, nothing distinctive to show).
const siteUrl = useRuntimeConfig().public.siteUrl
const skinCanonical = computed(() => `${siteUrl}/skins/${skin.value!.slug}`)
const skinTitle = computed(() => {
  const free = skin.value!.required_plan === 'free' ? 'free ' : ''
  // Long skin names blow past the ~60-char title budget; trim the name only
  const name = skin.value!.name.length > 28 ? `${skin.value!.name.slice(0, 27).trimEnd()}…` : skin.value!.name
  return `${name}: ${free}animated link-in-bio theme | mychar.gg`
})
const skinDescription = computed(() => {
  const s = skin.value!
  const bits: string[] = []
  const effect = (s.settings.background as any)?.effect
  if (effect && effect !== 'none') bits.push(`${effect} background effect`)
  const layout = s.settings.layout
  if (layout && layout !== 'default') bits.push(`${layout} layout`)
  if (s.tags?.length) bits.push(s.tags.slice(0, 3).join(', '))
  const detail = bits.length ? ` Features ${bits.join(', ')}.` : ''
  const creator = s.creator_username ? ` by @${s.creator_username}` : ''
  const full = `${s.name} is a customizable mychar.gg skin${creator}.${detail} Apply it to your link-in-bio page in one click.`
  // Meta descriptions over 160 chars get truncated in results and flagged
  return full.length > 160 ? `${full.slice(0, 157).trimEnd()}…` : full
})

useSeoMeta({
  title: () => skinTitle.value,
  titleTemplate: null,
  ogTitle: () => `${skin.value!.name} skin`,
  description: () => skinDescription.value,
  ogDescription: () => skinDescription.value,
  ogImage: () => skin.value!.background_url || undefined,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [{ key: 'canonical', rel: 'canonical', href: () => skinCanonical.value }]
})

// Quality gate: only index skins that have a preview background to show.
useRobotsRule(computed(() => skin.value?.background_url ? 'index, follow' : 'noindex, follow'))

useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' }),
  {
    '@type': 'CreativeWork',
    '@id': `${skinCanonical.value}#skin`,
    'name': skin.value.name,
    'url': skinCanonical.value,
    ...(skin.value.background_url ? { image: skin.value.background_url } : {}),
    ...(skin.value.creator_username ? { author: { '@type': 'Person', 'name': skin.value.creator_username } } : {}),
    ...(skin.value.tags?.length ? { keywords: skin.value.tags.join(', ') } : {})
  }
])

// Build mock character data
// Determine which sections the skin has enabled
const skinVisibility = computed(() => {
  const s = skinSettings.value
  const sections = (s.sections as Record<string, any>)?.visibility
  // Also check old theme keys for backward compat
  const themeSections = (s.theme as Record<string, any>)?.sectionVisibility
  return { ...(themeSections || {}), ...(sections || {}) }
})

const character = computed<CharacterPageData>(() => ({
  id: 'preview',
  uid: 0,
  username: skin.value!.name.toLowerCase().replace(/\s+/g, '-'),
  views: skin.value!.use_count,
  totalViews: skin.value!.use_count,
  block: null,
  // Set placeholder IDs for sections enabled in the skin so they render in the preview
  discord_user_id: skinVisibility.value.discord !== false ? 'preview' : null,
  youtube_channel_id: skinVisibility.value.youtube !== false ? 'preview' : null,
  twitch_user_id: skinVisibility.value.twitch !== false ? 'preview' : null,
  spotify_user_id: skinVisibility.value.spotify !== false ? 'preview' : null,
  plan: 'elite',
  customDomain: null
}))

// Build settings from skin data
const skinSettings = computed(() => skin.value!.settings || {})

// Resolve sections: new skins use `sections` key, old skins have it in `theme`
const skinSections = computed(() => {
  const s = skinSettings.value
  if (s.sections) return s.sections as Record<string, unknown>
  // Backward compat: extract from old theme keys
  const t = (s.theme || {}) as Record<string, unknown>
  if (t.sectionOrder || t.sectionVisibility || t.sectionTitles || t.showSectionTitles !== undefined) {
    return {
      order: t.sectionOrder,
      visibility: t.sectionVisibility,
      titles: t.sectionTitles,
      showTitles: t.showSectionTitles
    }
  }
  return {}
})

const settings = computed<CharacterPageSettings>(() => ({
  display_name: skin.value!.name,
  bio: 'This is a skin preview showing how your character page would look with this skin applied.',
  location: 'mychar.gg',
  pronouns: null,
  avatar_id: null,
  avatar_url: skin.value!.avatar_url,
  background_id: null,
  background_url: skin.value!.background_url,
  background_type: 'image',
  cursor_id: null,
  cursor_url: skin.value!.cursor_url,
  layout: (skinSettings.value.layout as string) || 'default',
  text: { ...DEFAULT_TEXT_SETTINGS, ...(skinSettings.value.text || {}) },
  background: { ...DEFAULT_BACKGROUND_SETTINGS, ...(skinSettings.value.background || {}) },
  audio: { ...DEFAULT_AUDIO_SETTINGS, ...(skinSettings.value.audio || {}) },
  theme: { ...DEFAULT_THEME_SETTINGS, ...(skinSettings.value.theme || {}) },
  sections: { ...DEFAULT_SECTION_SETTINGS, ...(skinSections.value || {}) },
  cursor: { ...DEFAULT_CURSOR_SETTINGS, ...(skinSettings.value.cursor || {}) },
  widgets: {
    audio: { ...DEFAULT_WIDGET_SETTINGS.audio, ...((skinSettings.value.widgets as any)?.audio || {}) },
    views: { ...DEFAULT_WIDGET_SETTINGS.views, ...((skinSettings.value.widgets as any)?.views || {}) },
    comments: { ...DEFAULT_WIDGET_SETTINGS.comments, ...((skinSettings.value.widgets as any)?.comments || {}) }
  },
  comments: { ...DEFAULT_COMMENT_SETTINGS },
  youtube: { ...DEFAULT_YOUTUBE_EMBED_SETTINGS },
  twitch: { ...DEFAULT_TWITCH_EMBED_SETTINGS },
  spotify: { ...DEFAULT_SPOTIFY_EMBED_SETTINGS },
  custom_font_url: skin.value!.custom_font_url || null,
  custom_font_id: null,
  setup_photo_url: null,
  displayed_friends: []
}))

const textSettings = computed(() => settings.value.text)
const backgroundSettings = computed(() => settings.value.background)
const audioSettings = computed(() => settings.value.audio)
const themeSettings = computed(() => settings.value.theme)

const audioPlaylist = computed<AudioPlaylistItem[]>(() =>
  (skin.value!.audio_tracks || []).map(t => ({
    id: t.id,
    original_name: t.original_name,
    url: t.url,
    position: t.position
  }))
)

const layoutComponent = computed(() => {
  const layout = settings.value.layout
  return LAYOUT_MAP[layout] || DefaultLayout
})

// Mock links to showcase link styling (linkStyle, iconStyle, iconSize)
const mockLinks: LinkItem[] = [
  {
    id: 'mock-1',
    link_id: 'twitch',
    value: 'https://twitch.tv/example',
    is_url: true,
    label: 'Twitch',
    icon_id: null,
    icon_url: null,
    position: 0,
    link: {
      id: 'twitch',
      name: 'Twitch',
      icon: 'i-simple-icons-twitch',
      icon_color: '#9146FF',
      placeholder: 'twitch.tv/username',
      input_placeholder: 'Your Twitch URL',
      prefix: 'https://twitch.tv/',
      allow_custom_icon: false,
      position: 0
    }
  },
  {
    id: 'mock-2',
    link_id: 'discord',
    value: 'https://discord.gg/example',
    is_url: true,
    label: 'Discord',
    icon_id: null,
    icon_url: null,
    position: 1,
    link: {
      id: 'discord',
      name: 'Discord',
      icon: 'i-simple-icons-discord',
      icon_color: '#5865F2',
      placeholder: 'discord.gg/invite',
      input_placeholder: 'Your Discord invite',
      prefix: 'https://discord.gg/',
      allow_custom_icon: false,
      position: 1
    }
  },
  {
    id: 'mock-3',
    link_id: 'youtube',
    value: 'https://youtube.com/@example',
    is_url: true,
    label: 'YouTube',
    icon_id: null,
    icon_url: null,
    position: 2,
    link: {
      id: 'youtube',
      name: 'YouTube',
      icon: 'i-simple-icons-youtube',
      icon_color: '#FF0000',
      placeholder: 'youtube.com/@channel',
      input_placeholder: 'Your YouTube URL',
      prefix: 'https://youtube.com/',
      allow_custom_icon: false,
      position: 2
    }
  },
  {
    id: 'mock-4',
    link_id: 'twitter',
    value: 'https://x.com/example',
    is_url: true,
    label: 'X / Twitter',
    icon_id: null,
    icon_url: null,
    position: 3,
    link: {
      id: 'twitter',
      name: 'X / Twitter',
      icon: 'i-simple-icons-x',
      icon_color: '#000000',
      placeholder: 'x.com/username',
      input_placeholder: 'Your X/Twitter URL',
      prefix: 'https://x.com/',
      allow_custom_icon: false,
      position: 3
    }
  },
  {
    id: 'mock-5',
    link_id: 'kick',
    value: 'https://kick.com/example',
    is_url: true,
    label: 'Kick',
    icon_id: null,
    icon_url: null,
    position: 4,
    link: {
      id: 'kick',
      name: 'Kick',
      icon: 'i-simple-icons-kick',
      icon_color: '#53FC18',
      placeholder: 'kick.com/username',
      input_placeholder: 'Your Kick URL',
      prefix: 'https://kick.com/',
      allow_custom_icon: false,
      position: 4
    }
  }
]

// Build achievements from real definitions - deterministic subset based on skin ID
function simpleHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

const mockAchievements = computed<CharacterAchievementItem[]>(() => {
  const defs = achievementData.value?.achievements || []
  if (defs.length === 0) return []
  const hash = simpleHash(skinId.value)
  const count = Math.min(defs.length, (hash % 2) + 2) // 2 or 3
  // Pick achievements starting at a deterministic offset
  const start = hash % defs.length
  const picked: typeof defs = []
  for (let i = 0; i < count; i++) {
    picked.push(defs[(start + i) % defs.length])
  }
  return picked.map((achievement, i) => ({
    id: `mock-b${i}`,
    achievement_id: achievement.id,
    is_displayed: true,
    display_position: i,
    granted_at: new Date().toISOString(),
    achievement
  }))
})

// Build games from real definitions - pick up to 3 with real logo_urls
const mockGames = computed<GameItem[]>(() => {
  const defs = gameData.value?.games || []
  if (defs.length === 0) return []
  return defs.slice(0, 3).map((game, i) => ({
    id: `mock-g${i}`,
    game_id: game.id,
    gamer_handle: 'SkinPreview',
    position: i,
    game
  }))
})

// Mock clips - skeleton thumbnails (no real URLs, ClipsDisplay will show placeholder bg)
const mockClips: ClipDisplayItem[] = [
  { id: 'mock-c1', url: null, title: 'Clutch Play', position: 0 },
  { id: 'mock-c2', url: null, title: 'Epic Moment', position: 1 },
  { id: 'mock-c3', url: null, title: 'Highlight Reel', position: 2 }
]

// Mock setup items
const mockSetupItems: SetupItem[] = [
  { id: 'mock-s1', name: 'RTX 4080 Super', value: 'GPU', category: 'pc', link: null, position: 0 },
  { id: 'mock-s2', name: 'Ryzen 9 7950X', value: 'CPU', category: 'pc', link: null, position: 1 },
  { id: 'mock-s3', name: 'LG 27GP950', value: '27" 4K 144Hz', category: 'monitor', link: null, position: 2 },
  { id: 'mock-s4', name: 'Wooting 60HE', value: 'Analog', category: 'keyboard', link: null, position: 3 },
  { id: 'mock-s5', name: 'Logitech G Pro X', value: 'Superlight 2', category: 'mouse', link: null, position: 4 }
]

// Mock friends - skeleton avatars (no URLs)
const mockFriends: FriendDisplayItem[] = [
  { character_id: 'mock-f1', username: 'Player1', avatar_url: null },
  { character_id: 'mock-f2', username: 'xGamer', avatar_url: null },
  { character_id: 'mock-f3', username: 'NightOwl', avatar_url: null },
  { character_id: 'mock-f4', username: 'FragMaster', avatar_url: null }
]
</script>

<template>
  <div>
    <!-- Preview banner -->
    <div class="fixed top-0 inset-x-0 z-9999 flex justify-center px-4 pt-3 pointer-events-none">
      <div class="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-elevated/50 backdrop-blur-xl border border-default shadow-lg shadow-black/20 text-sm text-white pointer-events-auto">
        <span class="flex items-center gap-1.5 text-primary-400">
          <UIcon
            name="i-lucide-eye"
            class="size-3.5"
          />
          <span class="text-[10px] font-semibold uppercase tracking-wider">Preview</span>
        </span>
        <span class="w-px h-4 bg-elevated" />
        <span>
          <strong class="font-medium">{{ skin!.name }}</strong>
          <NuxtLink
            v-if="skin!.creator_username"
            :to="`/${skin!.creator_username}`"
            class="text-dimmed ml-1 hover:text-toned hover:underline"
          >by @{{ skin!.creator_username }}</NuxtLink>
        </span>
        <span class="w-px h-4 bg-elevated" />
        <button
          type="button"
          class="flex items-center gap-1 px-3 py-1 rounded-full bg-elevated hover:bg-accented/60 text-xs text-toned hover:text-highlighted transition-all cursor-pointer"
          @click="goBack"
        >
          <UIcon
            name="i-lucide-arrow-left"
            class="size-3"
          />
          Back
        </button>
      </div>
    </div>

    <!-- Character page preview -->
    <div>
      <CharacterPageShell
        :character="character"
        :settings="settings"
        :text-settings="textSettings"
        :background-settings="backgroundSettings"
        :audio-settings="audioSettings"
        :theme-settings="themeSettings"
        :audio-playlist="audioPlaylist"
        :total-views="skin!.use_count || 42"
        :is-owner="false"
      >
        <component
          :is="layoutComponent"
          :character="character"
          :settings="settings"
          :text-settings="textSettings"
          :background-settings="backgroundSettings"
          :audio-settings="audioSettings"
          :theme-settings="themeSettings"
          :audio-playlist="audioPlaylist"
          :links="mockLinks"
          :achievements="mockAchievements"
          :games="mockGames"
          :clips="mockClips"
          :setup-items="mockSetupItems"
          :friends="mockFriends"
          :is-owner="false"
        />
      </CharacterPageShell>
    </div>
  </div>
</template>
